import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Clock, Search, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const statusOptions = [
  { value: '', label: 'Tous les statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Validée' },
  { value: 'rejected', label: 'Refusée' },
  { value: 'cancelled', label: 'Annulée' }
];

const sortOptions = [
  { value: 'recent', label: 'Plus récente' },
  { value: 'oldest', label: 'Plus ancienne' },
  { value: 'rest_date_nearest', label: 'Date de repos la plus proche' }
];

const statusBadge = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-300'
};

const normalizeStatus = (status) => (status === 'partial' ? 'pending' : (status || 'pending'));

const getStatusLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'approved') return 'Validée';
  if (normalized === 'rejected') return 'Refusée';
  if (normalized === 'cancelled') return 'Annulée';
  return 'En attente';
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR');
};

const formatTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default function AdminDemandes({ user, activeModule = 'b2b' }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDemandeId, setSelectedDemandeId] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [filters, setFilters] = useState({
    site_id: '',
    user_id: '',
    status: '',
    requested_date: '',
    rest_date: '',
    sort_by: 'recent'
  });

  const fetchDemandes = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const response = await axios.get(`${API}/demandes`, { withCredentials: true, params });
      setDemandes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch demandes:', error);
      setDemandes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemandes();
  }, [filters.site_id, filters.user_id, filters.status, filters.requested_date, filters.rest_date, filters.sort_by]);

  const selectedDemande = useMemo(() => {
    return demandes.find((d) => d.demande_id === selectedDemandeId) || null;
  }, [demandes, selectedDemandeId]);

  const siteOptions = useMemo(() => {
    const map = new Map();
    demandes.forEach((d) => {
      if (d.site_id || d.site_name) map.set(d.site_id || d.site_name, d.site_name || d.site_id);
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [demandes]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    demandes.forEach((d) => {
      if (d.user_id) map.set(d.user_id, d.user_name || d.user_email || d.user_id);
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [demandes]);

  const onApprove = async () => {
    if (!selectedDemande) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/demandes/${selectedDemande.demande_id}/approve`, { comment: '' }, { withCredentials: true });
      await fetchDemandes();
    } catch (error) {
      alert(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const onReject = async () => {
    if (!selectedDemande) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/demandes/${selectedDemande.demande_id}/reject`, {}, { withCredentials: true });
      await fetchDemandes();
    } catch (error) {
      alert(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const onSetPending = async () => {
    if (!selectedDemande || !selectedDemande.dates?.[0]) return;
    setProcessing(true);
    try {
      const restDate = selectedDemande.dates[0];
      await axios.put(
        `${API}/demandes/${selectedDemande.demande_id}/date/${restDate}/pending`,
        { comment: 'Demande remise en attente' },
        { withCredentials: true }
      );
      await fetchDemandes();
    } catch (error) {
      alert(`Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="admin-demandes-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="admin-demandes-title">Gestion des Demandes de Repos</h1>
          <p className="text-gray-500" data-testid="admin-demandes-subtitle">Filtres et suivi des actions (site/salarié/statut/dates)</p>
        </div>

        <Card data-testid="admin-demandes-filters-card">
          <CardHeader>
            <CardTitle className="text-base">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="site-filter">Site</Label>
                <select
                  id="site-filter"
                  value={filters.site_id}
                  onChange={(e) => setFilters((prev) => ({ ...prev, site_id: e.target.value }))}
                  className="w-full mt-1 border rounded-md px-3 py-2"
                  data-testid="admin-demandes-filter-site"
                >
                  <option value="">Tous les sites</option>
                  {siteOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="employee-filter">Salarié</Label>
                <select
                  id="employee-filter"
                  value={filters.user_id}
                  onChange={(e) => setFilters((prev) => ({ ...prev, user_id: e.target.value }))}
                  className="w-full mt-1 border rounded-md px-3 py-2"
                  data-testid="admin-demandes-filter-employee"
                >
                  <option value="">Tous les salariés</option>
                  {employeeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="status-filter">Statut</Label>
                <select
                  id="status-filter"
                  value={filters.status}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full mt-1 border rounded-md px-3 py-2"
                  data-testid="admin-demandes-filter-status"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="requested-date-filter">Date de demande</Label>
                <Input
                  id="requested-date-filter"
                  type="date"
                  value={filters.requested_date}
                  onChange={(e) => setFilters((prev) => ({ ...prev, requested_date: e.target.value }))}
                  data-testid="admin-demandes-filter-requested-date"
                />
              </div>

              <div>
                <Label htmlFor="rest-date-filter">Date de repos</Label>
                <Input
                  id="rest-date-filter"
                  type="date"
                  value={filters.rest_date}
                  onChange={(e) => setFilters((prev) => ({ ...prev, rest_date: e.target.value }))}
                  data-testid="admin-demandes-filter-rest-date"
                />
              </div>

              <div>
                <Label htmlFor="sort-filter">Tri</Label>
                <select
                  id="sort-filter"
                  value={filters.sort_by}
                  onChange={(e) => setFilters((prev) => ({ ...prev, sort_by: e.target.value }))}
                  className="w-full mt-1 border rounded-md px-3 py-2"
                  data-testid="admin-demandes-filter-sort"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="admin-demandes-table-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Search className="w-4 h-4" />
              Résultats ({demandes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-40 flex items-center justify-center" data-testid="admin-demandes-loading-state">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              </div>
            ) : demandes.length === 0 ? (
              <div className="text-center text-slate-500 py-10" data-testid="admin-demandes-empty-state">Aucune demande trouvée</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="admin-demandes-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-3 py-2">Site</th>
                      <th className="text-left px-3 py-2">Salarié</th>
                      <th className="text-left px-3 py-2">Date du repos demandé</th>
                      <th className="text-left px-3 py-2">Date de la demande</th>
                      <th className="text-left px-3 py-2">Statut</th>
                      <th className="text-left px-3 py-2">Traité par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demandes.map((demande) => {
                      const normalized = normalizeStatus(demande.status);
                      const treatedBy = demande.processed_by_name || demande.processed_by_email
                        ? `${demande.processed_by_name || ''}${demande.processed_by_name && demande.processed_by_email ? ' • ' : ''}${demande.processed_by_email || ''}`
                        : '—';
                      const restDate = demande.dates?.[0] || null;
                      const isSelected = selectedDemandeId === demande.demande_id;

                      return (
                        <tr
                          key={demande.demande_id}
                          className={`border-b cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                          onClick={() => setSelectedDemandeId(demande.demande_id)}
                          data-testid={`admin-demandes-row-${demande.demande_id}`}
                        >
                          <td className="px-3 py-2" data-testid={`admin-demandes-site-${demande.demande_id}`}>{demande.site_name || demande.site_id || 'Non assigné'}</td>
                          <td className="px-3 py-2" data-testid={`admin-demandes-employee-${demande.demande_id}`}>{demande.user_name || demande.user_email || demande.user_id}</td>
                          <td className="px-3 py-2" data-testid={`admin-demandes-rest-date-${demande.demande_id}`}>{formatDate(restDate)}</td>
                          <td className="px-3 py-2" data-testid={`admin-demandes-created-date-${demande.demande_id}`}>{formatDate(demande.created_at)}</td>
                          <td className="px-3 py-2" data-testid={`admin-demandes-status-${demande.demande_id}`}>
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full border ${statusBadge[normalized] || statusBadge.pending}`}>
                              {getStatusLabel(normalized)}
                            </span>
                          </td>
                          <td className="px-3 py-2" data-testid={`admin-demandes-treated-by-${demande.demande_id}`}>{treatedBy}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {selectedDemande && (
          <Card data-testid="admin-demandes-details-card">
            <CardHeader>
              <CardTitle className="text-base">Détail & actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <p data-testid="admin-demandes-details-status"><strong>Statut actuel:</strong> {getStatusLabel(selectedDemande.status)}</p>
                <p data-testid="admin-demandes-details-processed-at"><strong>Date/heure action:</strong> {formatDate(selectedDemande.processed_at)} {formatTime(selectedDemande.processed_at)}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={onApprove} disabled={processing} className="bg-green-600 hover:bg-green-700" data-testid="admin-demandes-approve-button">
                  <CheckCircle className="w-4 h-4 mr-2" /> Valider
                </Button>
                <Button onClick={onReject} disabled={processing} variant="destructive" data-testid="admin-demandes-reject-button">
                  <XCircle className="w-4 h-4 mr-2" /> Refuser
                </Button>
                {selectedDemande.status === 'pending' && (
                  <Button onClick={onSetPending} disabled={processing} variant="outline" data-testid="admin-demandes-pending-button">
                    <Clock className="w-4 h-4 mr-2" /> Remettre en attente
                  </Button>
                )}
              </div>

              <div className="pt-2 border-t" data-testid="admin-demandes-history-section">
                <p className="text-sm font-semibold mb-2">Historique des actions</p>
                <div className="space-y-2">
                  {(selectedDemande.history || []).slice().reverse().slice(0, 8).map((entry, idx) => (
                    <div key={`${entry.timestamp}-${idx}`} className="text-xs bg-slate-50 border border-slate-200 rounded p-2" data-testid={`admin-demandes-history-entry-${idx}`}>
                      <p>{formatDate(entry.timestamp)} - {formatTime(entry.timestamp)}</p>
                      <p className="font-medium">{entry.actor_name || entry.actor || '—'}{entry.actor_email ? ` (${entry.actor_email})` : ''}</p>
                      <p>
                        {entry.action === 'approved' ? 'Validation de la demande' :
                         entry.action === 'rejected' ? 'Refus de la demande' :
                         entry.action === 'cancelled_by_employee' ? 'Demande annulée par le salarié' :
                         entry.action === 'modified' ? 'Modification de la demande' :
                         'Création de la demande'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
